import { useEffect, useRef, useState, type RefObject } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useIsFetching } from "@tanstack/react-query";
import { Bell, Sun, Moon, ChevronDown, LogIn, LogOut, Menu, RefreshCw, UserRound } from "lucide-react";
import { useUiStore, type Language } from "@/stores/ui.store";
import { Avatar } from "@/ui/Avatar";
import { useCurrentUser, useLogout } from "@/features/auth/auth.hooks";
import { useUnreadNotificationsCount } from "@/features/notifications/notifications.hooks";
import { cn } from "@/lib/cn";
import { GlobalSearch } from "./GlobalSearch";

/**
 * Discreet "syncing" flag driven by TanStack Query's in-flight fetch count.
 * The delay keeps ORDINARY fetches invisible: with the stale-while-revalidate
 * cache, pages paint instantly from cache while a background refetch runs —
 * flashing "Synchronisation…" for those made every navigation FEEL slow even
 * though the content was already on screen. The badge now only appears when a
 * refresh genuinely drags (slow API), which is when it actually informs.
 */
function useIsSyncing(delayMs = 1_500): boolean {
  const fetching = useIsFetching();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (fetching === 0) {
      setShow(false);
      return;
    }
    const timer = setTimeout(() => setShow(true), delayMs);
    return () => clearTimeout(timer);
  }, [fetching, delayMs]);
  return show;
}

/**
 * Ferme le menu au pointerdown HORS de son conteneur. Remplace l'ancien
 * `onBlur` + setTimeout(120) : au tactile, le blur pouvait partir avant le
 * clic sur l'option (menu fermé sans action) ou ne jamais partir.
 */
function useDismissOnOutsidePress(
  ref: RefObject<HTMLElement>,
  open: boolean,
  onClose: () => void,
) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [ref, open, onClose]);
}

const LANGS: { code: Language; flag: string; label: string }[] = [
  { code: "fr", flag: "🇫🇷", label: "FR" },
  { code: "en", flag: "🇬🇧", label: "EN" },
  { code: "ar", flag: "🇩🇿", label: "AR" },
];

export function Topbar() {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const language = useUiStore((s) => s.language);
  const setLanguage = useUiStore((s) => s.setLanguage);
  const setNotificationsOpen = useUiStore((s) => s.setNotificationsOpen);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  const unreadCount = useUnreadNotificationsCount();
  const isSyncing = useIsSyncing();
  const [langOpen, setLangOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  useDismissOnOutsidePress(langRef, langOpen, () => setLangOpen(false));
  useDismissOnOutsidePress(userRef, userOpen, () => setUserOpen(false));

  const isDark = theme !== "light";
  const current = LANGS.find((l) => l.code === language) ?? LANGS[0]!;

  return (
    <header className="flex items-center gap-2 md:gap-3 px-3 md:px-5 h-[var(--topbar-h)] min-h-[var(--topbar-h)] bg-bg/95 border-b border-border relative z-50">
      {/* Mobile : le hamburger ouvre le volet de navigation (Sidebar off-canvas). */}
      <button
        type="button"
        onClick={toggleSidebar}
        aria-label={t("topbar.menu")}
        className="md:hidden flex h-10 w-10 -ms-1.5 shrink-0 items-center justify-center rounded-sm text-text2 hover:bg-bg4 hover:text-text transition-colors"
      >
        <Menu className="h-[18px] w-[18px]" />
      </button>
      <GlobalSearch />

      <div className="ms-auto flex items-center gap-1.5">
        {isSyncing && (
          <span
            role="status"
            aria-live="polite"
            className="flex items-center gap-1.5 me-1 text-[10.5px] font-medium text-text3 animate-fadeIn"
          >
            <RefreshCw className="h-3 w-3 animate-spin" />
            <span className="hidden sm:inline">{t("common.syncing")}</span>
          </span>
        )}
        <button
          onClick={() => setNotificationsOpen(true)}
          className="relative flex items-center justify-center h-9 w-9 max-md:h-10 max-md:w-10 rounded-sm text-text2 hover:bg-bg4 hover:text-text transition-colors"
          aria-label={t("topbar.notifications")}
        >
          <Bell className="h-[15px] w-[15px]" />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 end-1.5 h-1.5 w-1.5 rounded-full bg-danger ring-2 ring-bg" />
          )}
        </button>

        {/* Sous sm, le thème se règle depuis le menu utilisateur (place comptée). */}
        <button
          onClick={() => setTheme(isDark ? "light" : "dark")}
          className="hidden sm:flex items-center justify-center h-9 w-9 rounded-sm text-text2 hover:bg-bg4 hover:text-text transition-colors"
          aria-label={t("topbar.theme")}
        >
          {isDark ? <Sun className="h-[15px] w-[15px]" /> : <Moon className="h-[15px] w-[15px]" />}
        </button>

        <div className="hidden sm:block w-px h-[18px] bg-border mx-1" />

        <div className="relative" ref={langRef}>
          <button
            onClick={() => setLangOpen((v) => !v)}
            className="flex items-center gap-1.5 h-9 max-md:h-10 px-2.5 rounded-sm bg-bg3 border border-border text-[11px] font-semibold text-text2 hover:border-primary hover:text-primary transition-all"
          >
            <span>{current.flag}</span>
            <span className="hidden sm:inline">{current.label}</span>
            <ChevronDown className="hidden sm:block h-2.5 w-2.5" />
          </button>
          {langOpen && (
            <div className="absolute top-[calc(100%+6px)] end-0 min-w-[140px] bg-bg3 border border-border2 rounded p-1 z-[1000] shadow-floating animate-fadeIn">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => {
                    setLanguage(l.code);
                    setLangOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-2 rounded-sm text-[12.5px] hover:bg-bg4/70 transition-colors",
                    l.code === language ? "text-primary" : "text-text",
                  )}
                >
                  <span>{l.flag}</span>
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="hidden sm:block w-px h-[18px] bg-border mx-1" />

        {user ? (
          <div className="relative" ref={userRef}>
            <button
              onClick={() => setUserOpen((v) => !v)}
              className="flex items-center gap-2 h-9 max-md:h-10 ps-1 pe-2 rounded-sm hover:bg-bg4 transition-colors"
            >
              <Avatar name={user.name} size={24} />
              <span className="hidden md:inline text-[11px] font-semibold text-text2 max-w-[110px] truncate">
                {user.name}
              </span>
              <ChevronDown className="h-2.5 w-2.5 text-text3" />
            </button>
            {userOpen && (
              <div className="absolute top-[calc(100%+6px)] end-0 min-w-[180px] bg-bg3 border border-border2 rounded p-1 z-[1000] shadow-floating animate-fadeIn">
                <div className="px-3 py-2 border-b border-border mb-1">
                  <div className="text-[12px] font-semibold text-text truncate">{user.name}</div>
                  <div className="text-[10px] text-text3 truncate">{user.email}</div>
                </div>
                {/* Spec §6 — accès direct à « Mon profil » (aucune permission requise). */}
                <button
                  onClick={() => {
                    setUserOpen(false);
                    navigate("/profile");
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 rounded-sm text-[12.5px] text-text hover:bg-bg4/70 transition-colors"
                >
                  <UserRound className="h-3.5 w-3.5" /> {t("nav.profile")}
                </button>
                {/* Miroir du toggle thème masqué sous sm. */}
                <button
                  onClick={() => setTheme(isDark ? "light" : "dark")}
                  className="sm:hidden flex w-full items-center gap-2 px-3 py-2 rounded-sm text-[12.5px] text-text hover:bg-bg4/70 transition-colors"
                >
                  {isDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                  {t("topbar.theme")}
                </button>
                <button
                  onClick={() => {
                    logout();
                    navigate("/login");
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 rounded-sm text-[12.5px] text-danger hover:bg-bg4/70 transition-colors"
                >
                  <LogOut className="h-3.5 w-3.5" /> {t("common.logout")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-1.5 h-9 px-2.5 rounded-sm bg-bg3 border border-border text-[11px] font-semibold text-text2 hover:border-primary hover:text-primary transition-all"
          >
            <LogIn className="h-3 w-3" /> {t("common.login")}
          </button>
        )}
      </div>
    </header>
  );
}
